import { useState, useCallback, useMemo } from "react";
import type { MindMapNode, ExpandedMap } from "../types";

function getDepth(node: MindMapNode, level = 0): number {
  if (!node.children?.length) return level;
  return Math.max(...node.children.map((c) => getDepth(c, level + 1)));
}

function buildInitialExpanded(
  node: MindMapNode,
  level: number,
  collapseLevel: number
): ExpandedMap {
  const map: ExpandedMap = {};
  map[node.id] = level < collapseLevel;
  if (node.children) {
    for (const child of node.children) {
      Object.assign(map, buildInitialExpanded(child, level + 1, collapseLevel));
    }
  }
  return map;
}

export function useMindMapState(root: MindMapNode, initialCollapseLevel = 2) {
  const [expanded, setExpanded] = useState<ExpandedMap>(() =>
    buildInitialExpanded(root, 0, initialCollapseLevel)
  );

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const maxDepth = useMemo(() => getDepth(root), [root]);

  return { expanded, toggle, maxDepth };
}
